import { type LocalDate, compareDates } from "@despejo/domain";

/**
 * A row of public.parameter_versions as the engines receive it. Engines are
 * pure: callers pass every row they loaded, the engine picks the version in
 * force and reports whether it was DRAFT (which makes the result provisional).
 */
export interface EngineParameter {
  key: string;
  version?: number;
  effective_from: LocalDate;
  effective_to: LocalDate | null;
  value: Record<string, unknown>;
  review_status: "DRAFT" | "APPROVED" | "RETIRED";
}

export interface UsedParameter {
  key: string;
  version: number | null;
  effective_from: LocalDate;
  review_status: "DRAFT" | "APPROVED";
}

/** The version of `key` in force on `on` (latest effective_from; APPROVED wins a tie). RETIRED rows never apply. */
export function pickParameter(rows: readonly EngineParameter[], key: string, on: LocalDate): EngineParameter | null {
  const hits = rows
    .filter((r) => r.key === key && r.review_status !== "RETIRED")
    .filter((r) => compareDates(r.effective_from, on) <= 0 && (r.effective_to === null || compareDates(on, r.effective_to) <= 0))
    .sort((a, b) => b.effective_from.localeCompare(a.effective_from) || (a.review_status === "APPROVED" ? -1 : 0) - (b.review_status === "APPROVED" ? -1 : 0));
  return hits[0] ?? null;
}

export function usedParameter(p: EngineParameter): UsedParameter {
  return { key: p.key, version: p.version ?? null, effective_from: p.effective_from, review_status: p.review_status === "APPROVED" ? "APPROVED" : "DRAFT" };
}

/** Read a decimal-string field; numbers are refused so money never passes through a float. */
export function decimalField(v: Record<string, unknown>, field: string): string {
  const x = v[field];
  if (typeof x !== "string" || !/^-?\d+(\.\d+)?$/.test(x)) throw new TypeError(`parameter field ${field} must be a decimal string`);
  return x;
}
